import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Text from '../components/shared/Text';

function InvoiceShow() {
    const { id } = useParams();
    const [invoice, setInvoice] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [locations, setLocations] = useState<any[]>([]);
    const [logisticsCounts, setLogisticsCounts] = useState({
        rto: 0,
        installation: 0,
        finance: 0,
        insurance: 0,
        exchange: 0
    });

    useEffect(() => {
        loadInvoice();
        loadLocations();
    }, [id]);

    const loadLocations = async () => {
        try {
            const response = await api.get('/locations');
            setLocations(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    const loadLogisticsCounts = async (orderId: string) => {
        try {
            const [rtoRes, installRes, financeRes, insuranceRes, exchangeRes] = await Promise.all([
                api.get(`/orders/${orderId}/rtos`),
                api.get(`/orders/${orderId}/installations`),
                api.get(`/orders/${orderId}/finances`),
                api.get(`/orders/${orderId}/insurances`),
                api.get(`/orders/${orderId}/exchanges`)
            ]);

            setLogisticsCounts({
                rto: Array.isArray(rtoRes.data) ? rtoRes.data.length : (rtoRes.data?.data?.length || 0),
                installation: Array.isArray(installRes.data) ? installRes.data.length : (installRes.data?.data?.length || 0),
                finance: Array.isArray(financeRes.data) ? financeRes.data.length : (financeRes.data?.data?.length || 0),
                insurance: Array.isArray(insuranceRes.data) ? insuranceRes.data.length : (insuranceRes.data?.data?.length || 0),
                exchange: Array.isArray(exchangeRes.data) ? exchangeRes.data.length : (exchangeRes.data?.data?.length || 0)
            });
        } catch (err) {
            console.error('Error loading logistics counts:', err);
        }
    };

    const loadInvoice = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/invoices/${id}`);
            if (response.success) {
                setInvoice(response.data);
                if (response.data.order_id) {
                    loadLogisticsCounts(response.data.order_id);
                }
            } else {
                setError(response.message || 'Failed to load invoice');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center bg-slate-50">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <Text variant="body" color="slate" weight="medium">Loading invoice...</Text>
            </div>
        );
    }

    if (error) return <div className="mt-16 p-8 text-center text-rose-600 font-medium">Error: {error}</div>;
    if (!invoice) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Invoice not found</div>;

    const getStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            paid: 'bg-emerald-100 text-emerald-800',
            sent: 'bg-blue-100 text-blue-800',
            partially_paid: 'bg-yellow-100 text-yellow-800',
            cancelled: 'bg-gray-100 text-gray-800',
            draft: 'bg-gray-100 text-gray-800',
            overdue: 'bg-rose-100 text-rose-800'
        };
        return badges[status] || badges.draft;
    };

    const getOrderStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            delivered: 'bg-emerald-100 text-emerald-800',
            shipped: 'bg-blue-100 text-blue-800',
            processing: 'bg-indigo-100 text-indigo-800',
            confirmed: 'bg-amber-100 text-amber-800',
            cancelled: 'bg-gray-100 text-gray-800',
            pending: 'bg-gray-100 text-gray-800'
        };
        return badges[status] || badges.pending;
    };

    const userRole = (window as any).CURRENT_USER?.role || localStorage.getItem('user_role');
    const isCustomer = userRole === 'customer';
    const isPortalPath = location.pathname.startsWith('/portal');
    const locationName = locations.find(loc => loc.id === invoice.location_id)?.name || 'Primary Location';

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/50 min-h-screen">
            {/* Header / Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        {!isPortalPath && (
                            <Link to="/invoices" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            </Link>
                        )}
                        <Text variant="h1" color="primary">
                            Invoice #{invoice.invoice_number}
                        </Text>
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold capitalize ${getStatusBadge(invoice.status)}`}>
                            {invoice.status?.replace('_', ' ')}
                        </span>
                    </div>
                    <div className="flex items-center gap-1 ml-10">
                        <Text variant="body" color="slate">Billed to </Text>
                        <Text variant="body" color="primary" weight="semibold">{invoice.customer_name}</Text>
                        {invoice.order_id && (
                            <>
                                <Text variant="body" color="slate"> &bull; Based on </Text>
                                <Link to={isPortalPath ? `/portal/orders/${invoice.order_id}` : `/orders/${invoice.order_id}`} className="text-indigo-600 hover:underline">
                                    <Text variant="body" color="indigo" weight="semibold">Order #{invoice.order_number}</Text>
                                </Link>
                            </>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => {
                            const printWindow = window.open(`/invoices/${invoice.id}/print`, '_blank');
                            if (printWindow) {
                                printWindow.onload = () => printWindow.print();
                            }
                        }}
                        className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                        Print PDF
                    </button>
                    {!isCustomer && invoice.status !== 'cancelled' && invoice.status !== 'draft' && (
                        <Link
                            to={`/returns/new?invoice_id=${invoice.id}`}
                            className="px-4 py-2 bg-white border border-slate-200 text-rose-600 rounded-lg text-sm font-semibold hover:bg-rose-50 transition-colors shadow-sm"
                        >
                            Return Items
                        </Link>
                    )}
                    {!isCustomer && (
                        <Link
                            to={`/invoices/${invoice.id}/edit`}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            Edit
                        </Link>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Details & Items */}
                <div className="lg:col-span-2 space-y-8">
                    
                    {/* Invoice Basic Details */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                        <div className="p-5 flex-1">
                            <Text variant="label" color="slate" className="mb-1">Issue Date</Text>
                            <Text variant="body" color="primary" weight="semibold">
                                {new Date(invoice.invoice_date).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                            </Text>
                        </div>
                        <div className="p-5 flex-1">
                            <Text variant="label" color="slate" className="mb-1">Due Date</Text>
                            <Text variant="body" color="primary" weight="semibold">
                                {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'Immediate'}
                            </Text>
                        </div>
                        <div className="p-5 flex-1">
                            <Text variant="label" color="slate" className="mb-1">Payment Hub</Text>
                            <Text variant="body" color="primary" weight="semibold" className="capitalize">
                                {invoice.payment_mode?.replace('_', ' ') || 'None'}
                            </Text>
                        </div>
                        <div className="p-5 flex-1">
                            <Text variant="label" color="slate" className="mb-1">Location</Text>
                            <Text variant="body" color="primary" weight="semibold">{locationName}</Text>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                            <Text variant="h3" color="primary">Line Items</Text>
                            <Text variant="small" weight="semibold" color="slate" className="bg-white px-2.5 py-0.5 rounded border border-slate-200">
                                {invoice.items?.length || 0} items
                            </Text>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-white border-b border-slate-100">
                                        <th className="px-6 py-3"><Text variant="label" color="slate">Item</Text></th>
                                        <th className="px-6 py-3 text-center"><Text variant="label" color="slate">Qty</Text></th>
                                        <th className="px-6 py-3 text-right"><Text variant="label" color="slate">Price</Text></th>
                                        <th className="px-6 py-3 text-right"><Text variant="label" color="slate">Total</Text></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {invoice.items?.map((item: any) => (
                                        <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <Text variant="body" weight="semibold" color="primary">{item.product_name}</Text>
                                                    {item.requires_installation && (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-black uppercase tracking-tighter shadow-sm animate-pulse">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                            Install
                                                        </span>
                                                    )}
                                                </div>
                                                {item.product_unit_identifier && (
                                                    <Text variant="mono" color="slate" className="text-xs mt-1">SN: {item.product_unit_identifier}</Text>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Text variant="mono" weight="bold" color="slate" className="inline-block px-2.5 py-1 bg-slate-100 rounded text-xs">
                                                    {item.quantity}
                                                </Text>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Text variant="body" color="slate">₹{item.unit_price?.toLocaleString()}</Text>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Text variant="body" weight="semibold" color="primary">₹{item.total?.toLocaleString()}</Text>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Linkages */}
                    {invoice.order_id && (
                        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                                <Text variant="h3" color="primary">Operational Linkages</Text>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                                    <div>
                                        <Text variant="label" color="slate" className="mb-1">Source Order</Text>
                                        <Link to={`/orders/${invoice.order_id}`} className="text-indigo-600 hover:underline">
                                            <Text variant="body" weight="bold" color="indigo">#{invoice.order_number}</Text>
                                        </Link>
                                    </div>
                                    {invoice.order_status && (
                                        <div>
                                            <Text variant="label" color="slate" className="mb-1">Order Status</Text>
                                            <span className={`px-2 py-0.5 rounded text-[11px] font-bold capitalize ${getOrderStatusBadge(invoice.order_status)}`}>
                                                {invoice.order_status}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    {[
                                        { label: 'Vehicles', path: 'rto', count: logisticsCounts.rto },
                                        { label: 'Installs', path: 'installation', count: logisticsCounts.installation },
                                        { label: 'Finance', path: 'finance', count: logisticsCounts.finance },
                                        { label: 'Insurance', path: 'insurance', count: logisticsCounts.insurance },
                                        { label: 'Exchanges', path: 'exchange', count: logisticsCounts.exchange }
                                    ].filter(s => s.count > 0).map((service) => (
                                        <Link
                                            key={service.path}
                                            to={`/orders/${invoice.order_id}/${service.path}`}
                                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-50 hover:border-slate-300 transition-colors"
                                        >
                                            <Text variant="small" weight="semibold" color="slate">{service.label}</Text>
                                            <Text variant="mono" className="bg-slate-100 text-slate-600 px-1.5 rounded text-[10px]">{service.count}</Text>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Notes & Terms */}
                    {(invoice.notes || invoice.terms) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {invoice.notes && (
                                <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-5">
                                    <Text variant="label" className="text-amber-800 mb-2">Remarks</Text>
                                    <Text variant="body" className="text-amber-900 whitespace-pre-wrap">{invoice.notes}</Text>
                                </div>
                            )}
                            {invoice.terms && (
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                                    <Text variant="label" color="slate" className="mb-2">Terms & Conditions</Text>
                                    <Text variant="small" color="slate" className="whitespace-pre-wrap tracking-wide">{invoice.terms}</Text>
                                </div>
                            )}
                        </div>
                    )}

                </div>

                {/* Right Column - Totals */}
                <div className="lg:col-span-1">
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sticky top-6">
                        <Text variant="h3" color="primary" className="mb-6">Payment Summary</Text>
                        
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <Text variant="body" color="slate" weight="medium">Subtotal</Text>
                                <Text variant="body" color="primary" weight="semibold">₹{invoice.subtotal?.toLocaleString()}</Text>
                            </div>
                            
                            {(invoice.total_igst || 0) > 0 ? (
                                <div className="flex justify-between items-center">
                                    <Text variant="body" color="slate" weight="medium">IGST</Text>
                                    <Text variant="body" color="primary" weight="semibold">₹{invoice.total_igst?.toLocaleString()}</Text>
                                </div>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center">
                                        <Text variant="body" color="slate" weight="medium">CGST</Text>
                                        <Text variant="body" color="primary" weight="semibold">₹{(invoice.total_cgst || (invoice.tax_amount || 0) / 2).toLocaleString()}</Text>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <Text variant="body" color="slate" weight="medium">SGST</Text>
                                        <Text variant="body" color="primary" weight="semibold">₹{(invoice.total_sgst || (invoice.tax_amount || 0) / 2).toLocaleString()}</Text>
                                    </div>
                                </>
                            )}

                            {invoice.discount_amount > 0 && (
                                <div className="flex justify-between items-center">
                                    <Text variant="body" color="slate" weight="medium">Discount</Text>
                                    <Text variant="body" weight="semibold" className="text-rose-600">-₹{invoice.discount_amount?.toLocaleString()}</Text>
                                </div>
                            )}

                            <div className="h-px bg-slate-100 my-4"></div>

                            <div className="flex justify-between items-end">
                                <Text variant="h3" color="primary">Grand Total</Text>
                                <Text variant="h1" color="primary" className="text-2xl">₹{invoice.total_amount?.toLocaleString()}</Text>
                            </div>

                            <div className="pt-4 mt-4 border-t border-slate-100 space-y-3">
                                <div className="flex justify-between items-center">
                                    <Text variant="small" weight="semibold" className="text-emerald-600">Amount Paid</Text>
                                    <Text variant="body" weight="bold" className="text-emerald-600">₹{invoice.paid_amount?.toLocaleString()}</Text>
                                </div>
                                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    <Text variant="label" color="slate">Balance</Text>
                                    <Text variant="h2" className={invoice.balance_due > 0 ? 'text-rose-600' : 'text-slate-900'}>
                                        ₹{invoice.balance_due?.toLocaleString()}
                                    </Text>
                                </div>
                            </div>
                        </div>

                        {/* Payment Details Section */}
                        <div className="mt-6 pt-6 border-t border-slate-100">
                            <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                                <Text variant="body" weight="bold" color="primary">Payment Details</Text>
                            </h3>
                            <div className="space-y-3">
                                {/* Payment Status */}
                                <div className="flex items-center justify-between">
                                    <Text variant="small" color="slate" weight="medium">Status</Text>
                                    {(() => {
                                        const paid = invoice.paid_amount || 0;
                                        const total = invoice.total_amount || 0;
                                        const isPaid = invoice.status === 'paid' || paid >= total;
                                        const isPartial = paid > 0 && paid < total;
                                        if (isPaid) return (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[11px] font-bold">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                Paid
                                            </span>
                                        );
                                        if (isPartial) return (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[11px] font-bold">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                                Partial
                                            </span>
                                        );
                                        return (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-[11px] font-bold">
                                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                                Pending
                                            </span>
                                        );
                                    })()}
                                </div>

                                {/* Payment Method */}
                                {invoice.payment_mode && (
                                    <div className="flex items-center justify-between">
                                        <Text variant="small" color="slate" weight="medium">Method</Text>
                                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                                            {invoice.payment_mode === 'cash' && '💵'}
                                            {invoice.payment_mode === 'card' && '💳'}
                                            {invoice.payment_mode === 'upi' && '📱'}
                                            {invoice.payment_mode === 'cheque' && '🏦'}
                                            {invoice.payment_mode === 'bank_transfer' && '🏛️'}
                                            <Text variant="small" weight="semibold" color="primary" className="capitalize">{invoice.payment_mode.replace('_', ' ')}</Text>
                                        </span>
                                    </div>
                                )}

                                {/* Transaction ID */}
                                {invoice.transaction_id && (
                                    <div className="flex items-start justify-between gap-2">
                                        <Text variant="small" color="slate" weight="medium" className="flex-shrink-0">Txn ID</Text>
                                        <Text variant="mono" weight="bold" color="primary" className="text-xs text-right break-all">{invoice.transaction_id}</Text>
                                    </div>
                                )}

                                {/* Cheque Number */}
                                {invoice.cheque_number && (
                                    <div className="flex items-center justify-between">
                                        <Text variant="small" color="slate" weight="medium">Cheque No.</Text>
                                        <Text variant="mono" weight="bold" color="primary" className="text-xs">{invoice.cheque_number}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InvoiceShow;
